import React, { useRef, useEffect, useState } from 'react';
import promptsData from '../../data/prompts.json';
import './promptsContainer.css';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import RequestSvc from '../../services/RequestSvc';

export default function PromptsContainer({ group = 'landing_prompts', onSelect }) {
  const [prompts, setPrompts] = useState(promptsData[group] || []);
  const [limitMessage, setLimitMessage] = useState('');
  const rowRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;
    const load = async () => {
      try {
        try {
          const requestSvc = new RequestSvc();
          const limitResponse = await requestSvc.get('https://pulpoia-ops.com/backend/ads/user-feature-limits?featurePlanCode=-Bus');
          if (isMounted) {
            if (limitResponse?.message === 'Has llegado al límite') {
              setLimitMessage(`${limitResponse.message} de tus búsquedas con IA por este mes. Contacta con soporte para actualizar tu plan`);
            } else if (limitResponse?.data) {
              const limite = Number(limitResponse.data.Limite);
              const totalUsed = Number(limitResponse.data.TotalUsed);
              if (Number.isFinite(limite) && Number.isFinite(totalUsed) && limite - totalUsed < 5) {
                setLimitMessage('Cuidado. Te quedan menos de 5 búsquedas con IA');
              }
            }
          }
        } catch (limitErr) {
          console.warn('Failed to load feature limits.', limitErr);
        }

        const res = await fetch('https://pulpoia-ops.com/backend/ads/prompts', { signal: controller.signal, cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => ({}));
        // data expected to be an object with groups like the local JSON
        const remote = data?.[group];
        if (Array.isArray(remote) && remote.length > 0) {
          setPrompts(remote);
        } else if (data && typeof data === 'object') {
          // if group missing, try to merge or fallback to top-level translations
          setPrompts(promptsData[group] || []);
        }
      } catch (err) {
        // on error, keep local fallback
        console.warn('Failed to load remote prompts, using local data.', err);
        setPrompts(promptsData[group] || []);
      }
    };

    load();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [group]);

  const scrollBy = (dir = 1) => {
    const el = rowRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6; // scroll by 60% of visible width
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  return (
    <div className="prompts-wrapper" style={{ background: '#000', borderRadius: 8 }}>
      {limitMessage && (
        <div
          role="alert"
          style={{
            background: '#32b5f1',
            color: '#1f1f1f',
            padding: '8px 12px',
            borderRadius: 6,
            margin: '8px 8px 0',
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <span>{limitMessage}</span>
          <button
            type="button"
            onClick={() => setLimitMessage('')}
            aria-label="Cerrar"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1f1f1f',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}
      <button className="prompts-arrow left" onClick={() => scrollBy(-1)} aria-label="Anterior">
        <ArrowBackIosIcon fontSize="small" />
      </button>
      <div className="prompts-row" ref={rowRef} aria-label="Prompts carousel">
        {prompts.map((p, i) => (
          <div
            key={i}
            className="prompts-item"
            tabIndex={0}
            role="button"
            onClick={() => onSelect && onSelect(p)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect && onSelect(p); } }}
          >
            {p}
          </div>
        ))}
      </div>
      <button className="prompts-arrow right" onClick={() => scrollBy(1)} aria-label="Siguiente">
        <ArrowForwardIosIcon fontSize="small" />
      </button>
    </div>
  );
}
